import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Check, AlertCircle, Loader2, KeyRound, X } from 'lucide-react';
import apiService from '../../services/api';

// ─── Password rules. Only the length is required, matching the backend
// (authController changePassword / setUserPassword) and the mobile app; the
// other two are tips that raise the strength meter. ───
const MIN_LENGTH = 8;
const RULES = [
  { id: 'len',    label: `At least ${MIN_LENGTH} characters`, test: pw => pw.length >= MIN_LENGTH, required: true },
  { id: 'letter', label: 'Letters and numbers',               test: pw => /[A-Za-z]/.test(pw) && /\d/.test(pw) },
  { id: 'symbol', label: 'A symbol, or 12+ characters',       test: pw => /[^A-Za-z0-9]/.test(pw) || pw.length >= 12 },
];

function strengthOf(pw) {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  if (pw.length < MIN_LENGTH) return { score: 1, label: 'Too short', color: 'var(--bad-text, #dc2626)' };
  let score = 1;
  if (/[A-Za-z]/.test(pw) && /\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (score <= 2) return { score: 1, label: 'Weak',   color: 'var(--bad-text, #dc2626)' };
  if (score === 3) return { score: 2, label: 'Okay',   color: 'var(--warn-text, #b45309)' };
  if (score === 4) return { score: 3, label: 'Good',   color: 'var(--ok-text, #15803d)' };
  return              { score: 4, label: 'Strong', color: 'var(--ok-text, #15803d)' };
}

const STYLES = `
  .pw-form { display: flex; flex-direction: column; gap: 14px; }
  .pw-field { display: flex; flex-direction: column; gap: 6px; }
  .pw-label { font-size: 12.5px; font-weight: 600; color: var(--text-2, #504d47); }
  .pw-input-wrap { position: relative; }
  .pw-input {
    width: 100%; height: 42px; box-sizing: border-box; padding: 0 44px 0 12px;
    border: 1px solid var(--field-border, #e8e5dc); border-radius: 10px;
    background: var(--surface-2, #faf9f6); color: var(--text, #1a1917); font-size: 14px; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .pw-input:focus { background: var(--surface, #fff); border-color: var(--accent, #012B63); box-shadow: 0 0 0 3px var(--tint, rgba(1,43,99,0.10)); }
  .pw-input[aria-invalid="true"] { border-color: var(--bad-border, #fca5a5); }
  .pw-eye {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%); width: 32px; height: 32px;
    display: inline-flex; align-items: center; justify-content: center; border: none; border-radius: 8px;
    background: transparent; color: var(--text-muted, #8c8779); cursor: pointer;
  }
  .pw-eye:hover { background: var(--border-soft, #f2f0ea); color: var(--text, #1a1917); }
  .pw-meter { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; margin-top: 2px; }
  .pw-meter span { height: 4px; border-radius: 2px; background: var(--border-soft, #f2f0ea); transition: background 0.2s; }
  .pw-rules { display: flex; flex-wrap: wrap; gap: 6px 14px; margin: 0; padding: 0; list-style: none; }
  .pw-rules li { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted, #8c8779); }
  .pw-rules li[data-ok="true"] { color: var(--ok-text, #15803d); }
  .pw-rules li span.dot { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid currentColor; display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; }
  .pw-msg { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-radius: 10px; font-size: 13px; }
  .pw-msg-ok  { background: var(--ok-bg, #f0fdf4);  border: 1px solid var(--ok-border, #bbf7d0);  color: var(--ok-text, #15803d); }
  .pw-msg-bad { background: var(--bad-bg, #fef2f2); border: 1px solid var(--bad-border, #fecaca); color: var(--bad-text, #dc2626); }
  .pw-btn {
    height: 42px; padding: 0 18px; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
    border: 1px solid #FFC81E; border-radius: 10px; background: #FFC81E; color: #1C1A14;
    font-size: 13.5px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  }
  .pw-btn:hover:not(:disabled) { background: #F5A300; border-color: #F5A300; }
  .pw-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  .pw-btn-ghost { background: var(--surface, #fff); color: var(--text-2, #504d47); border-color: var(--border, #e8e5dc); }
  .pw-btn-ghost:hover:not(:disabled) { background: var(--border-soft, #f2f0ea); }
  @keyframes pwSpin { to { transform: rotate(360deg); } }
  @keyframes pwPop { from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
`;

function PasswordInput({ id, label, value, onChange, invalid, autoFocus, autoComplete = 'new-password', describedBy }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw-field">
      <label className="pw-label" htmlFor={id}>{label}</label>
      <div className="pw-input-wrap">
        <input
          id={id}
          className="pw-input"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          spellCheck={false}
        />
        <button
          type="button"
          className="pw-eye"
          onClick={() => setShow(v => !v)}
          aria-label={show ? 'Hide password' : 'Show password'}
          title={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

// ─── The form itself. isSelf = the signed-in user changing their own
// password; otherwise an admin is setting a new one for `employee`. ───
// Someone else's password: admins never type it for them. They send a reset
// link to the person's email, and the person chooses their own password.
function ResetLinkPanel({ employee }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const firstName = employee?.firstName || employee?.name?.split(' ')[0] || 'They';
  async function send() {
    setState('sending');
    try {
      await apiService.forgotPassword(employee.email);
      setState('sent');
    } catch {
      setState('error');
    }
  }
  return (
    <div className="pw-form">
      <style>{STYLES}</style>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
        Send {firstName} a link to choose a new password. It goes to <b>{employee?.email}</b> and works once, for a limited time.
        Their current password keeps working until they use it.
      </p>
      {state === 'sent' && <div className="pw-msg pw-msg-ok"><Check size={15} /> Reset link sent to {employee?.email}.</div>}
      {state === 'error' && <div className="pw-msg pw-msg-bad"><AlertCircle size={15} /> The link couldn't be sent. Please try again.</div>}
      <div>
        <button type="button" className="pw-btn" onClick={send} disabled={state === 'sending' || !employee?.email}>
          {state === 'sending' ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><KeyRound size={15} /> {state === 'sent' ? 'Send again' : 'Send reset link'}</>}
        </button>
      </div>
    </div>
  );
}

export function PasswordPanel({ employee, isSelf, onDone }) {
  if (!isSelf) return <ResetLinkPanel employee={employee} />;
  return <OwnPasswordPanel employee={employee} isSelf onDone={onDone} />;
}

function OwnPasswordPanel({ employee, isSelf, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [status,   setStatus]   = useState('idle'); // idle | saving | saved | error
  const [error,    setError]    = useState('');
  const savedTimer = useRef(null);
  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const firstName = employee?.firstName || employee?.name?.split(' ')[0] || 'this person';
  const rulesOk   = RULES.filter(r => r.required).every(r => r.test(password));
  const matches   = password.length > 0 && password === confirm;
  const canSave   = rulesOk && matches && status !== 'saving';
  const strength  = strengthOf(password);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSave) return;
    setStatus('saving');
    setError('');
    try {
      // Own password: POST /auth/change-password. It signs out other sessions
      // and returns fresh tokens for this device, which api.changePassword saves.
      // Someone else's: PUT /auth/users/:id/password (admins), which signs them out.
      if (isSelf) await apiService.changePassword({ newPassword: password, confirmPassword: confirm });
      else        await apiService.setUserPassword(employee.id, { newPassword: password, confirmPassword: confirm });
      setStatus('saved');
      setPassword('');
      setConfirm('');
      savedTimer.current = setTimeout(() => { setStatus('idle'); onDone?.(); }, 1800);
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.message || "Couldn't update the password. Please try again.");
    }
  }

  return (
    <form className="pw-form" onSubmit={handleSubmit} noValidate>
      <style>{STYLES}</style>

      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-2, #6b6760)', lineHeight: 1.55 }}>
        {isSelf
          ? "Choose a new password for your account. You'll stay signed in here, and be signed out on your other devices."
          : `Set a new password for ${firstName}. They'll be signed out everywhere and need the new password to sign back in.`}
      </p>

      <PasswordInput
        id="pw-new"
        label="New password"
        value={password}
        onChange={v => { setPassword(v); if (status === 'error') setStatus('idle'); }}
        autoFocus
        describedBy="pw-rules"
      />

      {password && (
        <div aria-live="polite">
          <div className="pw-meter" aria-hidden="true">
            {[1, 2, 3, 4].map(i => <span key={i} style={i <= strength.score ? { background: strength.color } : undefined} />)}
          </div>
          <p style={{ margin: '5px 0 0', fontSize: 12, fontWeight: 600, color: strength.color }}>{strength.label}</p>
        </div>
      )}

      <ul className="pw-rules" id="pw-rules">
        {RULES.map(r => {
          const ok = r.test(password);
          return (
            <li key={r.id} data-ok={ok}>
              <span className="dot">{ok && <Check size={9} strokeWidth={3.5} />}</span>
              {r.label}
            </li>
          );
        })}
      </ul>

      <PasswordInput
        id="pw-confirm"
        label="Confirm new password"
        value={confirm}
        onChange={v => { setConfirm(v); if (status === 'error') setStatus('idle'); }}
        invalid={confirm.length > 0 && !matches}
      />
      {confirm.length > 0 && !matches && (
        <p style={{ margin: '-6px 0 0', fontSize: 12, color: 'var(--bad-text, #dc2626)' }}>The passwords don't match yet.</p>
      )}

      {status === 'saved' && (
        <div className="pw-msg pw-msg-ok" role="status">
          <Check size={15} /> {isSelf ? 'Your password has been updated.' : `${firstName}'s password has been updated.`}
        </div>
      )}
      {status === 'error' && (
        <div className="pw-msg pw-msg-bad" role="alert">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button type="submit" className="pw-btn" disabled={!canSave}>
          {status === 'saving'
            ? <><Loader2 size={15} style={{ animation: 'pwSpin 1s linear infinite' }} /> Saving…</>
            : <><KeyRound size={15} /> {isSelf ? 'Update password' : 'Set password'}</>}
        </button>
      </div>
    </form>
  );
}

// ─── Stand-alone dialog for the signed-in user, e.g. from the avatar or
// settings menu: <ChangePasswordModal onClose={() => setOpen(false)} /> ───
export function ChangePasswordModal({ onClose }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <style>{STYLES}</style>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(28,26,20,0.28)', backdropFilter: 'blur(2px)' }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-modal-title"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 201,
          width: 'min(420px, calc(100vw - 32px))', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', boxSizing: 'border-box',
          background: 'var(--surface, #fff)', border: '1px solid var(--border, #e8e5dc)', borderRadius: 18,
          boxShadow: '0 24px 70px rgba(28,26,20,0.22)', padding: 24,
          animation: 'pwPop 0.2s cubic-bezier(0.22, 1, 0.36, 1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--tint, #EEF3FB)', color: 'var(--accent, #012B63)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <KeyRound size={17} />
            </span>
            <h2 id="pw-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text, #1a1917)' }}>Change password</h2>
          </div>
          <button type="button" className="pw-eye" style={{ position: 'static', transform: 'none' }} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <PasswordPanel isSelf onDone={onClose} />
      </div>
    </>
  );
}

export default ChangePasswordModal;